import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { supabase } from "services/supabase";
import { useSelector, useDispatch } from "react-redux";
import {
  getSupabasePreferences,
  selectPreferencesLoaded,
  selectPreferencesLoading,
  selectPreferencesInitialized,
} from "store/PreferenceSlice";
import {
  fetchUserSubscription,
  selectSubscriptionLoaded,
  clearSubscription,
} from "store/SubscriptionSlice";
import { trackSignUpForGtm } from "../utils/gtm";

const SupabaseAuthContext = createContext();

export const useSupabaseAuth = () => {
  return useContext(SupabaseAuthContext);
};

export const SupabaseAuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preferencesLoadingState, setPreferencesLoadingState] = useState(false);
  const dispatch = useDispatch();
  const initializedRef = useRef(false);

  // Check preferences state to avoid unnecessary API calls
  const preferencesLoading = useSelector(selectPreferencesLoading);
  const preferencesLoaded = useSelector(selectPreferencesLoaded);
  const preferencesInitialized = useSelector(selectPreferencesInitialized);

  // Check subscription state to avoid unnecessary API calls
  const subscriptionLoaded = useSelector(selectSubscriptionLoaded);

  // Clear preferences loading state when preferences are loaded
  useEffect(() => {
    if (preferencesLoaded && preferencesLoadingState) {
      setPreferencesLoadingState(false);
    }
  }, [preferencesLoaded, preferencesLoadingState]);

  useEffect(() => {
    // Prevent multiple initializations
    if (initializedRef.current) return;
    initializedRef.current = true;

    // Get initial session (and handle magic-link callback if token is in URL)
    const getInitialSession = async () => {
      try {
        // If Supabase redirected here with token_hash (e.g. admin login link), exchange it for a session
        if (typeof window !== "undefined" && window.location) {
          const params = new URLSearchParams(window.location.search);
          const hashParams = new URLSearchParams(
            window.location.hash?.replace(/^#/, "") || ""
          );
          const tokenHash =
            params.get("token_hash") || hashParams.get("token_hash");
          const type =
            params.get("type") ||
            hashParams.get("type") ||
            (tokenHash ? "email" : null);
          if (tokenHash && type) {
            const { error } = await supabase.auth.verifyOtp({
              token_hash: tokenHash,
              type: type === "magiclink" ? "magiclink" : "email",
            });
            if (!error) {
              // Clean URL so the token isn't re-used or visible
              const search = new URLSearchParams(window.location.search);
              const hash = new URLSearchParams(
                window.location.hash?.replace(/^#/, "") || ""
              );
              search.delete("token_hash");
              search.delete("type");
              hash.delete("token_hash");
              hash.delete("type");
              const q = search.toString();
              const h = hash.toString();
              const cleanUrl =
                window.location.pathname +
                (q ? `?${q}` : "") +
                (h ? `#${h}` : "");
              window.history.replaceState(null, "", cleanUrl);
            }
          }
        }

        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session?.user) {
          setCurrentUser(session.user);
          // Clear signup pending email from localStorage since user is verified
          localStorage.removeItem("fractional_signup_pending_email");
          // Fetch preferences on initial load/refresh if not already loaded or loading
          if (
            !preferencesLoaded &&
            !preferencesLoading &&
            !preferencesInitialized
          ) {
            setPreferencesLoadingState(true);
            dispatch(getSupabasePreferences(session.user.id));
          }
          // Fetch subscription on initial load/refresh if not already loaded
          if (!subscriptionLoaded) {
            dispatch(fetchUserSubscription({ userId: session.user.id }));
          }
        }
      } catch (err) {
        console.error("SupabaseAuthContext getInitialSession:", err);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      trackSignUpForGtm(event, session);
      if (session?.user) {
        setCurrentUser(session.user);
        // Clear signup pending email from localStorage when user session is established
        // This happens when user verifies their email and gets redirected back
        localStorage.removeItem("fractional_signup_pending_email");
        // Only fetch preferences if not already loaded and not currently loading
        if (
          !preferencesLoaded &&
          !preferencesLoading &&
          !preferencesInitialized
        ) {
          setPreferencesLoadingState(true);
          dispatch(getSupabasePreferences(session.user.id));
        }
        // Fetch subscription if not already loaded
        if (!subscriptionLoaded) {
          dispatch(fetchUserSubscription({ userId: session.user.id }));
        }
      } else {
        setCurrentUser(null);
        // Clear subscription on logout
        dispatch(clearSubscription());
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [dispatch]);

  const logout = useCallback(async () => {
    try {
      // Sign out from all sessions (global scope)
      const { error } = await supabase.auth.signOut({ scope: "global" });
      if (error) {
        console.error("Error signing out:", error.message);
        return { error };
      }
      // Ensure currentUser is cleared immediately
      setCurrentUser(null);
      return { error: null };
    } catch (error) {
      console.error("Unexpected error during logout:", error);
      // Still clear currentUser even if there's an error
      setCurrentUser(null);
      return { error };
    }
  }, []);

  const signInWithGoogle = useCallback(async (intendedDestination = null) => {
    // Store intended destination in localStorage as backup
    if (intendedDestination) {
      try {
        localStorage.setItem("oauth_intended_destination", intendedDestination);
      } catch (error) {
        console.warn("Failed to store intended destination:", error);
      }
    }

    // Build redirect URL - include intended destination as query param if provided
    let redirectTo = `${window.location.origin}/sign-in`;
    if (intendedDestination) {
      redirectTo += `?from=${encodeURIComponent(intendedDestination)}`;
    }

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo,
      },
    });
    return { data, error };
  }, []);

  const signUpWithGoogle = useCallback(async (intendedDestination = null) => {
    // Store intended destination in localStorage as backup
    if (intendedDestination) {
      try {
        localStorage.setItem("oauth_intended_destination", intendedDestination);
      } catch (error) {
        console.warn("Failed to store intended destination:", error);
      }
    }

    // Build redirect URL - include intended destination as query param if provided
    let redirectTo = `${window.location.origin}/sign-in`;
    if (intendedDestination) {
      redirectTo += `?from=${encodeURIComponent(intendedDestination)}`;
    }

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo,
      },
    });
    return { data, error };
  }, []);

  const signInWithAzure = useCallback(async (intendedDestination = null) => {
    // Store intended destination in localStorage as backup
    if (intendedDestination) {
      try {
        localStorage.setItem("oauth_intended_destination", intendedDestination);
      } catch (error) {
        console.warn("Failed to store intended destination:", error);
      }
    }

    // Build redirect URL - include intended destination as query param if provided
    let redirectTo = `${window.location.origin}/sign-in`;
    if (intendedDestination) {
      redirectTo += `?from=${encodeURIComponent(intendedDestination)}`;
    }

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "azure",
      options: {
        redirectTo,
        scopes: "email",
      },
    });
    return { data, error };
  }, []);

  const signUpWithAzure = useCallback(async (intendedDestination = null) => {
    // Store intended destination in localStorage as backup
    if (intendedDestination) {
      try {
        localStorage.setItem("oauth_intended_destination", intendedDestination);
      } catch (error) {
        console.warn("Failed to store intended destination:", error);
      }
    }

    // Build redirect URL - include intended destination as query param if provided
    let redirectTo = `${window.location.origin}/sign-in`;
    if (intendedDestination) {
      redirectTo += `?from=${encodeURIComponent(intendedDestination)}`;
    }

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "azure",
      options: {
        redirectTo,
        scopes: "email",
      },
    });
    return { data, error };
  }, []);

  const signInWithEmailPassword = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    // If sign-in is successful, immediately update currentUser from the session
    // This ensures the user state is updated even if onAuthStateChange is delayed
    if (!error && data?.session?.user) {
      setCurrentUser(data.session.user);
      // Fetch preferences if not already loaded
      if (
        !preferencesLoaded &&
        !preferencesLoading &&
        !preferencesInitialized
      ) {
        setPreferencesLoadingState(true);
        dispatch(getSupabasePreferences(data.session.user.id));
      }
      // Fetch subscription if not already loaded
      if (!subscriptionLoaded) {
        dispatch(fetchUserSubscription({ userId: data.session.user.id }));
      }
    }
    return { data, error };
  }, [preferencesLoaded, preferencesLoading, preferencesInitialized, subscriptionLoaded, dispatch]);

  const signUpWithEmailPassword = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
        // Disable email confirmation if it's causing database errors
        // Note: This should be configured in Supabase Dashboard under Authentication > Settings
      },
    });
    
    // Log detailed error information for debugging
    if (error) {
      console.error("Sign-up error details:", {
        message: error.message,
        status: error.status,
        name: error.name,
      });
    }
    
    // If sign-up is successful, immediately update currentUser from the session
    // This ensures the user state is updated even if onAuthStateChange is delayed
    // Note: If email confirmation is required, session might be null initially
    if (!error && data?.session?.user) {
      setCurrentUser(data.session.user);
      // Fetch preferences if not already loaded
      if (
        !preferencesLoaded &&
        !preferencesLoading &&
        !preferencesInitialized
      ) {
        setPreferencesLoadingState(true);
        dispatch(getSupabasePreferences(data.session.user.id));
      }
      // Fetch subscription if not already loaded
      if (!subscriptionLoaded) {
        dispatch(fetchUserSubscription({ userId: data.session.user.id }));
      }
    } else if (!error && data?.user && !data?.session) {
      // User created but email confirmation required
      // The user will be automatically signed in after email confirmation
      console.log("User created successfully. Email confirmation may be required.");
    }
    return { data, error };
  }, [preferencesLoaded, preferencesLoading, preferencesInitialized, subscriptionLoaded, dispatch]);

  const contextValue = useMemo(
    () => ({
      currentUser,
      userId: currentUser?.id,
      preferencesLoading: preferencesLoadingState || preferencesLoading,
      preferencesLoaded,
      logout,
      signInWithGoogle,
      signUpWithGoogle,
      signInWithAzure,
      signUpWithAzure,
      signInWithEmailPassword,
      signUpWithEmailPassword,
    }),
    [
      currentUser,
      preferencesLoadingState,
      preferencesLoading,
      preferencesLoaded,
      logout,
      signInWithGoogle,
      signUpWithGoogle,
      signInWithAzure,
      signUpWithAzure,
      signInWithEmailPassword,
      signUpWithEmailPassword,
    ]
  );

  return (
    <SupabaseAuthContext.Provider value={contextValue}>
      {!loading && children}
    </SupabaseAuthContext.Provider>
  );
};
